import { useQuery } from '@tanstack/react-query';
import { companyRelationshipApi } from 'shared/services/companyRelationshipApi';
import { queryKeys } from '../queryConfig';

/**
 * A Provider company can only create Fichas de Servicio, Evaluaciones
 * Iniciales, and Permisos de Trabajo in its OWN company once at least one
 * linked client grants `allowProviderSelfService` (see providerSelfServiceGate.ts
 * on the BE — already enforced at create time on all three endpoints). This
 * surfaces that same gate to the FE so StaffTareas/LibraryTabs/the provider
 * dashboard can lock those entry points proactively instead of a 403 after
 * the fact. Also exposes the linked-clients' user-allocation budget
 * (canAddUsers/userAllocation), previously only checked when a client
 * assigns an existing provider user, never when the provider creates a new one.
 *
 * Only meaningful for a Provider (companyType 'proveedor') company_admin —
 * disabled otherwise, defaulting to fully-open so client-side pages never
 * see this hook lock anything.
 */
export function useProviderSelfServiceStatus(companyId?: string | null, companyType?: string | null) {
  const enabled = !!companyId && companyType === 'proveedor';
  const query = useQuery({
    queryKey: queryKeys.providerSelfServiceStatus.byCompany(companyId || ''),
    // companyId is only honored by the BE for platform staff (super_admin/
    // safety_staff) viewing someone else's provider — for a company_admin
    // caller it's ignored and the BE always resolves their own company.
    queryFn: () => companyRelationshipApi.getSelfServiceStatus(companyId || undefined),
    enabled,
    staleTime: 60 * 1000,
  });

  const userCreation = query.data?.userCreation ?? null;

  return {
    canSelfServe: enabled ? !!query.data?.canSelfServe : true,
    // Company-management actions (add external staff, subcontract, host
    // visitas) are gated ONLY by an own active plan — no client grant can
    // unlock them, so this is deliberately not folded into canSelfServe.
    // Defaults to true (open) when disabled, same as canSelfServe.
    hasOwnActivePlan: enabled ? !!query.data?.hasOwnActivePlan : true,
    accountExecutive: enabled ? (query.data?.accountExecutive ?? null) : null,
    userCreation,
    userCreationBlocked: enabled && !!userCreation?.blocked,
    loading: enabled && query.isLoading,
  };
}
